import {Trans} from '@lingui/macro';
import {compact, filter} from 'lodash';
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dictionary} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {matchEmail} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useTimeout} from '../../../../srcCommon/helpers/hookHelpers';
import {buildTeammateDisplayName, isTeammateActive, isTeammatePending} from '../../../helpers/modelHelpers';
import {RoleModel, SpecialRoleAliasesEnum} from '../../../models/api/roleModel';
import {TeammateLicenseTypesEnum, TeammateModel} from '../../../models/api/teammateModel';
import {TeamModel} from '../../../models/api/teamModel';
import {postAsync} from '../../../services/http/http';
import {HttpResponseError} from '../../../services/http/httpError';
import {
  InterfaceEventsEnum,
  InviteTeammatePopoverAccessPointsEnum,
  trackInterfaceEvent,
} from '../../../services/tracking';
import {EmailInviteToken} from '../../common/editor/tokens/emailInviteToken';
import {TokensEditor} from '../../common/editor/tokens/tokensEditor';
import {
  InviteTeammatePopoverDropdown,
  InviteTeammatePopoverDropdownItem,
} from './inviteTeammatePopoverDropdown';

/*
 * Props.
 */

interface InviteTeammatePopoverFormProps {
  /** Where the popover was accessed from. */
  accessPoint: InviteTeammatePopoverAccessPointsEnum | undefined;
  /** Base URL of company's endpoints. */
  companyUrl: string;
  /** Closes the popover. */
  closePopover: Handler;
  /** Includes pending, active, and blocked teammates. */
  cloneableTeammates: ReadonlyArray<TeammateModel>;
  /** Includes pending and active teammates. */
  existingTeammatesInCompany: ReadonlyArray<TeammateModel>;
  /** Default and custom roles. Actual grantable roles is a subset of these. */
  roles: ReadonlyArray<RoleModel>;
  /** The team the user will become a member of. */
  team: TeamModel;
  /** The user. Used for tracking purposes. */
  user: TeammateModel;
}

/*
 * Types.
 */

interface ErrorState {
  /** Company-wide error message such as trial plan exceeded. */
  globalMessage: string;
  /**
   * A map of emails to errors specific to that email, if any. Emails without
   * any errors won't be in the map.
   */
  perEmailInviteErrors: Dictionary<string>;
}

type TeamRoleDropdownOption = InviteTeammatePopoverDropdownItem<string, string>;
type CloneDropdownOption = InviteTeammatePopoverDropdownItem<number, TeammateModel | undefined>;

/*
 * Constants.
 */

/** Used to select the option to not clone. */
const doNotCloneDropdownOptionKey = -1;

/** Used to extract emails from the error message inside parentheses. */
const parenthesesMatchRegexp = /\(([^)]+)\)/;

/**
 * Commas, tabs, newlines, or semicolons. Users type in one of these symbols to
 * indicate they want to type in a new email.
 *
 * We also add space, since the tokens editor converts Enter key presses into
 * spaces, and we want Enter to trigger tokenization.
 */
const delimiterRegexp = /[,;\t\n ]+/;

/*
 * Style.
 */

const headerStyles = () => css`
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 20px;
`;

const StyledContentDiv = styled.div`
  & > :not(:last-child) {
    padding-bottom: 20px;
  }
`;

const StyledEmailsInputHeaderDiv = styled.div`
  ${headerStyles}
  padding-bottom: 7px;
`;

const StyledEmailsInputDiv = styled.div`
  border-radius: 8px;
  padding: 8px;
  display: flex;

  box-shadow: 0 0 0 1px ${(p) => p.theme.palette.blue.shade40},
    inset 0 0 0 1px ${(p) => p.theme.palette.blue.shade40};
`;

const StyledEmailTokensWrapper = styled.div`
  flex: 1;
  align-self: center;
  margin: -4px;
`;

const StyledTokensEditor = styled(TokensEditor)`
  color: ${(p) => p.theme.greys.shade80};
  padding-left: 6px;
  margin: 0 !important;
  min-height: 32px;
`;

const StyledErrorMessageDiv = styled.div`
  max-height: 200px;
  margin-top: 8px;
  background: ${(p) => p.theme.palette.red.shade20};
  border-radius: 6px;
  padding: 8px;

  color: ${(p) => p.theme.palette.red.shade40};
  line-height: 14px;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
`;

const StyledDropdownOptionWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 150px;
  grid-template-rows: 20px 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 2px;
  grid-template-areas:
    'header .'
    'description dropdown';
`;

const StyledDropdownOptionHeaderDiv = styled.div`
  grid-area: header;
  ${headerStyles()}
`;

const StyledDropdownOptionDescriptionDiv = styled.div`
  grid-area: description;
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade50};
  line-height: 20px;
`;

const StyledTeamRoleDropdownDiv = styled.div`
  grid-area: dropdown;
`;

const StyledFooterDiv = styled.div`
  display: flex;
  padding-top: 48px;
  justify-content: flex-end;

  & > *:not(:last-child) {
    margin-right: 10px;
  }
`;

const StyledInviteButtonContentDiv = styled.div`
  width: 96px;
  display: flex;
  justify-content: center;
`;

const StyledSuccessfulInviteDiv = styled.div`
  width: 120px;
  line-height: 17px;
  padding: 6px 12px 7px 12px;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.palette[PaletteColorsEnum.GREEN].shade50};
  background: ${(p) => p.theme.palette[PaletteColorsEnum.GREEN].shade10};
  display: flex;
  justify-content: center;
  border-radius: 100px;

  ${(p) => boxShadow(p.theme, `0 1px 1px 0 rgba(0, 0, 0, 0.04)`)};
  & > :first-child {
    margin-left: 8px;
  }
`;

/*
 * Component.
 */

export const InviteTeammatePopoverForm: FC<InviteTeammatePopoverFormProps> = (props) => {
  /* Form state. */

  // Dirty is the conventional term to denote a form has been changed in some
  // meaningful way. Generally, we only want to show errors after the user has
  // done something like add an email or try to submit the form.
  const [isDirty, setIsDirty] = useState(false);

  // Whether the user has successfully submitted the form.
  const [isInviteSuccessful, setIsInviteSuccessful] = useState(false);
  const [setSafeTimeout] = useTimeout();

  const [errorState, setErrorState] = useState<ErrorState>({globalMessage: '', perEmailInviteErrors: {}});

  // Possibly a custom role or one of our default special roles.
  const [teamRoleToGrant, setTeamRoleToGrant] = useState<string>(SpecialRoleAliasesEnum.ADMIN);

  const [teammateToClone, setTeammateToClone] = useState<TeammateModel | undefined>();

  // Raw value in the input. Every time an email is tokenized, it's removed from
  // the raw value.
  const [rawEmailsInputText, setRawEmailsInputText] = useState<string>('');
  // Tokenized emails, extracted from the raw input value.
  const [emailsToInvite, setEmailsToInvite] = useState<ReadonlyArray<string>>([]);

  // This is a local cache of teammates from other companies we have tried to
  // invite.
  const [existingTeammateEmailsFromAnotherCompany, setExistingTeammateEmailsFromAnotherCompany] = useState<
    ReadonlyArray<string>
  >([]);
  const {existingTeammatesInCompany, accessPoint, user} = props;

  /* Tracking. */

  /** Wraps event tracking with the access point and other context. */
  const trackInviteTeammatePopoverFormEvent = useCallback(
    (eventName: InterfaceEventsEnum, overrides = {}) => {
      trackInterfaceEvent(eventName, {accessPoint, ...overrides});
    },
    [accessPoint],
  );

  /* Tokens editor methods and variables. */

  const tokensEditorRef = React.useRef<TokensEditor>(null);

  /** Handler for when user selects a token and deletes it. */
  const removeEmails = (tokenIndexesToRemove: ReadonlyArray<number>) => {
    const newEmails = filter(emailsToInvite, (_r, index) => !tokenIndexesToRemove.includes(index));
    setEmailsToInvite(newEmails);
  };

  /** Renders each email as a token. */
  const renderEmail = (emailIndex: number, isSelected: boolean) => {
    const email = emailsToInvite[emailIndex];
    // If there's no entry for the email in the error state, then the email is
    // valid.
    //
    // If invite was successful, then there is this period of time where we
    // timeout to close the popover. In that time, Pusher will likely update
    // Redux with the newly-invited teammates, which will cause some of the
    // emails in the input to become duplicates. This is why we hide all errors
    // if the user successfully submitted the form.
    const hasError = Boolean(errorState.perEmailInviteErrors[email]) && !isInviteSuccessful;

    return <EmailInviteToken email={email} isSelected={isSelected} hasError={hasError} />;
  };

  /** Handler of user text changes. */
  const onTextChange = (newText: string) => {
    const [newEmails, remainingText] = parseEmailsFromText(newText);
    if (newEmails.length > 0) {
      setEmailsToInvite([...emailsToInvite, ...newEmails]);
      setIsDirty(true);
    }

    setRawEmailsInputText(remainingText);
  };

  /** Tries to parse emails from text. */
  const onBlur = () => {
    // Append whitespace to guarantee there is a delimiter.
    const [newEmails, remainingText] = parseEmailsFromText(`${rawEmailsInputText} `);
    if (newEmails.length > 0) {
      setEmailsToInvite([...emailsToInvite, ...newEmails]);
      setIsDirty(true);
    }

    // Remove artificially added whitespace.
    setRawEmailsInputText(remainingText.trim());
  };

  /** Recalculates errors whenever existing teammates or emails changes. */
  const updateErrors = useCallback(() => {
    const newErrorState = computeNewErrorState(
      emailsToInvite,
      existingTeammateEmailsFromAnotherCompany,
      existingTeammatesInCompany,
    );

    setErrorState(newErrorState);
  }, [emailsToInvite, existingTeammateEmailsFromAnotherCompany, existingTeammatesInCompany]);

  useEffect(() => {
    updateErrors();
  }, [updateErrors]);

  useEffect(() => {
    // Track whenever the form is opened.
    trackInviteTeammatePopoverFormEvent(InterfaceEventsEnum.INVITE_TEAMMATE_POPOVER_SHOWN);
  }, [trackInviteTeammatePopoverFormEvent]);

  /**
   * Form submission handler.
   *
   * Makes request to mass_invite endpoint. If we receive errors in the
   * response, set the error state. Otherwise, show the success state and close
   * the popover after 2 seconds.
   *
   * TODO: can wrap the popover with some animation in the future.
   */
  const onSubmit = async () => {
    setIsDirty(true);
    if (computeErrorMessage(errorState)) {
      return;
    }

    const inviteTeammateUrl = `${props.companyUrl}/mass_invite`;

    try {
      const access = {
        // By default, we make the user a global member and not a company admin.
        global: {role: SpecialRoleAliasesEnum.MEMBER},
        [props.team.namespace]: {
          role: teamRoleToGrant,
          inbox_ids: undefined,
        },
      };

      const inviteRequestBody = {
        teammates: emailsToInvite.map((email) => ({
          email,
          access,
          given_name: '',
          family_name: '',
          license_type: TeammateLicenseTypesEnum.FULL,
          raw_text: '',
          teammates_to_invite: null,
          clone: buildCloneOptionsForInviteBlob(teammateToClone),
          should_compute_inbox_access: true,
        })),
      };

      await postAsync(inviteTeammateUrl, inviteRequestBody);

      setIsInviteSuccessful(true);
      setSafeTimeout(() => {
        props.closePopover();
      }, 2000);

      trackInviteTeammatePopoverFormEvent(InterfaceEventsEnum.INVITE_TEAMMATE_POPOVER_SUCCESS, {
        isCompanyAdmin: Boolean(user.access.global?.roles?.includes(SpecialRoleAliasesEnum.ADMIN)),
        numTeammatesInvited: inviteRequestBody.teammates.length,
        teamRoleGranted: teamRoleToGrant,
        didClone: Boolean(teammateToClone),
      });
    } catch (err) {
      setIsInviteSuccessful(false);
      if (err instanceof HttpResponseError && err.status === 409 && err.code === 'teammates_already_exist') {
        // Teammates we tried to invite are part of other companies.
        const existingTeammatesStringMatch = parenthesesMatchRegexp.exec(err.reason);
        if (existingTeammatesStringMatch) {
          setExistingTeammateEmailsFromAnotherCompany(existingTeammatesStringMatch[1].split(', '));
        }
      } else if (
        err instanceof HttpResponseError &&
        err.status === 403 &&
        err.code === 'too_many_teammates'
      ) {
        // Exceeded trial limit on number of teammates. This is 15 by default.
        setErrorState((currentErrorState) => ({
          ...currentErrorState,
          globalMessage: err.reason,
        }));
      } else {
        // Some other error we don't recognize.
        setErrorState((currentErrorState) => ({
          ...currentErrorState,
          globalMessage: (err && err.reason) || 'An error occurred. Please email support@frontapp.com',
        }));
      }

      trackInviteTeammatePopoverFormEvent(InterfaceEventsEnum.INVITE_TEAMMATE_POPOVER_HTTP_ERROR, {err});
    }
  };

  return (
    <form>
      <StyledContentDiv>
        <div>
          <StyledEmailsInputHeaderDiv>
            <Trans id="invite-teammate-popover-emails-header">Emails</Trans>
          </StyledEmailsInputHeaderDiv>
          <StyledEmailsInputDiv>
            <StyledEmailTokensWrapper data-testid="invite-teammate-popover-emailsinput">
              <StyledTokensEditor
                placeholder="Email addresses separated by commas"
                inputId="invite-teammate-popover-emails-input"
                ref={tokensEditorRef}
                shouldFocus
                tokenIds={computeEmailTokenIds(emailsToInvite)}
                renderToken={renderEmail}
                text={rawEmailsInputText}
                onTextChange={onTextChange}
                onBlur={onBlur}
                onRemoveTokensClick={removeEmails}
              />
            </StyledEmailTokensWrapper>
          </StyledEmailsInputDiv>
          {maybeRenderErrorMessage(errorState, isDirty, isInviteSuccessful)}
        </div>
        <StyledDropdownOptionWrapperDiv>
          <StyledDropdownOptionHeaderDiv>
            <Trans id="invite-teammate-popover-teamrole-header">Team role</Trans>
          </StyledDropdownOptionHeaderDiv>
          <StyledDropdownOptionDescriptionDiv>
            <Trans id="invite-teammate-popover-teamrole-description">
              Grant permission to access team inboxes and other resources
            </Trans>
          </StyledDropdownOptionDescriptionDiv>

          <StyledTeamRoleDropdownDiv>
            <InviteTeammatePopoverDropdown
              items={buildTeamRoleDropdownOptions(props.roles)}
              selectedItemKey={teamRoleToGrant}
              onSelect={(item: TeamRoleDropdownOption) => setTeamRoleToGrant(item.value)}
            />
          </StyledTeamRoleDropdownDiv>
        </StyledDropdownOptionWrapperDiv>

        <StyledDropdownOptionWrapperDiv>
          <StyledDropdownOptionHeaderDiv>
            <Trans id="invite-teammate-popover-clone-header">Clone from teammate</Trans>
          </StyledDropdownOptionHeaderDiv>
          <StyledDropdownOptionDescriptionDiv>
            <Trans id="invite-teammate-popover-clone-description">
              Copy settings and resources from another user
            </Trans>
          </StyledDropdownOptionDescriptionDiv>

          <StyledTeamRoleDropdownDiv>
            <InviteTeammatePopoverDropdown
              items={buildCloneDropdownOptions(props.cloneableTeammates)}
              selectedItemKey={teammateToClone ? teammateToClone.id : doNotCloneDropdownOptionKey}
              onSelect={(item: CloneDropdownOption) => setTeammateToClone(item.value)}
            />
          </StyledTeamRoleDropdownDiv>
        </StyledDropdownOptionWrapperDiv>
      </StyledContentDiv>

      <StyledFooterDiv data-testid="invite-teammate-popover-footer">
        <Button
          interactionId="invite-teammate-popover-cancelbutton-clicked"
          variant={ButtonVariantsEnum.SECONDARY}
          onClick={props.closePopover}
        >
          <Trans id="invite-teammate-popover-cancel">Cancel</Trans>
        </Button>
        {renderInviteButton(isInviteSuccessful, onSubmit)}
      </StyledFooterDiv>
    </form>
  );
};

/*
 * Helpers.
 */

function renderInviteButton(isInviteSuccessful: boolean, onSubmit: Handler) {
  if (isInviteSuccessful) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledSuccessfulInviteDiv>
            <Trans id="invite-teammate-popover-invitesuccess">Invited!</Trans>
            <Icon name="checkmarkSelectedSmall" size={10} color={theme.palette.green.shade50} />
          </StyledSuccessfulInviteDiv>
        )}
      </UserThemeConsumer>
    );
  }

  return (
    <Button
      interactionId="invite-teammate-popover-invitebutton-clicked"
      variant={ButtonVariantsEnum.PRIMARY}
      onClick={onSubmit}
    >
      <StyledInviteButtonContentDiv>
        <Trans id="invite-teammate-popover-invite">Invite to Front</Trans>
      </StyledInviteButtonContentDiv>
    </Button>
  );
}

/**
 * Returns a map of emails to their errors. Need a map instead of just the first
 * error because we want to highlight which email tokens are invalid in red.
 */
function computeNewErrorState(
  emailsToInvite: ReadonlyArray<string>,
  existingTeammateEmailsFromAnotherCompany: ReadonlyArray<string>,
  existingTeammatesInCompany: ReadonlyArray<TeammateModel>,
) {
  const newErrorState: ErrorState = {
    globalMessage: '',
    perEmailInviteErrors: {},
  };

  emailsToInvite.forEach((email) => {
    if (existingTeammateEmailsFromAnotherCompany.find((e) => e === email)) {
      newErrorState.perEmailInviteErrors[email] = `"${email}" already has a Front account`;
      return;
    }

    const existingTeammateForEmail = existingTeammatesInCompany.find((t) => t.email === email);
    if (!existingTeammateForEmail) {
      return;
    }

    if (isTeammateActive(existingTeammateForEmail)) {
      newErrorState.perEmailInviteErrors[email] = `"${email}" is already a member of your company`;
      return;
    }

    if (isTeammatePending(existingTeammateForEmail)) {
      newErrorState.perEmailInviteErrors[email] = `"${email}" is already invited`;
    }
  });

  if (emailsToInvite.length < 1) {
    newErrorState.globalMessage = 'Need at least one email';
  }

  return newErrorState;
}

/**
 * Takes a string and tries to parse out email tokens.
 *
 * Returns any parsed email tokens plus the remaining text which we couldn't
 * parse emailsToInvite tokens out of.
 */
function parseEmailsFromText(text: string): [string[], string] {
  const tokens = text.split(delimiterRegexp);
  const minTokens = 2;

  if (tokens.length < minTokens) {
    return [[], text];
  }

  const parsedEmails = tokens.map(matchEmail);

  const emailsToAdd = compact(parsedEmails);
  if (emailsToAdd.length < 1) {
    return [[], text];
  }

  const invalidTokens = tokens.filter((_t, index) => !parsedEmails[index]);
  const remainingText = invalidTokens.join(',');

  return [emailsToAdd, remainingText];
}

/** Maybe renders the error message. */
function maybeRenderErrorMessage(errorState: ErrorState, isDirty: boolean, isInviteSuccessful: boolean) {
  // Only try to render if form has been submitted at least once or user has
  // added an email.
  if (!isDirty || isInviteSuccessful) {
    return null;
  }

  const errorMessage = computeErrorMessage(errorState);
  if (!errorMessage) {
    return null;
  }

  return <StyledErrorMessageDiv>{errorMessage}</StyledErrorMessageDiv>;
}

/** Returns the error message to display, or the empty string. */
function computeErrorMessage(errorState: ErrorState) {
  if (errorState.globalMessage) {
    return errorState.globalMessage;
  }

  const perEmailInviteErrors = Object.values(errorState.perEmailInviteErrors);
  if (perEmailInviteErrors.length > 0) {
    return perEmailInviteErrors[0];
  }

  return '';
}

/** Builds list of roles that can be granted to invited teammates. */
function buildTeamRoleDropdownOptions(roles: ReadonlyArray<RoleModel>) {
  const customRoles = roles.filter((role) => !role.isSpecial);

  // We only want the Admin and Member special roles, plus any custom roles the
  // company has.
  return [
    {
      key: SpecialRoleAliasesEnum.ADMIN,
      value: SpecialRoleAliasesEnum.ADMIN,
      name: <Trans id="invite-teammate-popover-roleoption-admin">Admin</Trans>,
    },
    {
      key: SpecialRoleAliasesEnum.MEMBER,
      value: SpecialRoleAliasesEnum.MEMBER,
      name: <Trans id="invite-teammate-popover-roleoption-member">Member</Trans>,
    },
    ...customRoles.map((role) => ({key: role.alias, value: role.alias, name: role.name})),
  ];
}

/** Builds clone options for the final invite blob for a teammate. */
function buildCloneOptionsForInviteBlob(teammateToClone: TeammateModel | undefined) {
  // We default to cloning everything in the popover for simplicity's sake.
  const baseOptions = {
    should_clone_answers: true,
    should_clone_notifications: true,
    should_clone_preferences: true,
    should_clone_signatures: true,
    teams_inboxes: true,
    workspace: true,
  };

  if (teammateToClone) {
    return {...baseOptions, teammate_id: teammateToClone.id};
  }

  return baseOptions;
}

/** Build list of options to clone from. First option is None. */
function buildCloneDropdownOptions(cloneableTeammates: ReadonlyArray<TeammateModel>) {
  return [
    {
      name: <Trans id="invite-teammate-popover-cloneoption-none">None</Trans>,
      key: doNotCloneDropdownOptionKey,
      value: undefined,
    },
    ...cloneableTeammates.map((t) => ({
      name: buildTeammateDisplayName(t),
      key: t.id,
      value: t,
    })),
  ];
}

/**
 * Computes token IDs so the token editor can correctly highlight selected
 * tokens.
 */
function computeEmailTokenIds(emails: ReadonlyArray<string>) {
  // Append index to email so each token is unique, otherwise if you type in
  // multiple duplicate emails, highlighting one will highlight all the
  // duplicates.
  return emails.map((email, index) => `${email}-${index}`);
}
